import { enemyByKind } from "./enemy-defs.js";
/**
 * The in-game screen: canvas viewport + HUD + input handling. Implements the
 * RuneScape-style interaction model (see actions.ts): left click = primary
 * action under the cursor; right click = context menu; hover readout shows
 * what a click would do. The client only picks intentions from its own
 * observation — the server is authoritative on everything.
 *
 * Rendering, the cosmetic interpolation, the hover/left-click/right-click
 * input, and the frame loop all live in the shared Viewport (viewport.ts) —
 * the arena is a host over the same Viewport, so the hero moves, animates,
 * and is interacted with identically in both and they can't drift apart. The
 * Game is a thin host: it plugs in the game-specific bits (primary action =
 * walk/interact/enter, the context-menu verbs, lobby names, events → chat,
 * the HUD/chat/map overlay).
 */
import { CLASS_DEFS, ClientOp, consumableById, REASON, RETURN_PORTAL_TARGET_ID, ServerOp, TILE_AIMED_KINDS, floorById, itemByKind, parseAppearance, roomById } from "@pd/shared";
import type { EquipSlot, InteractKind, Observation, ObservedPlayer, PartyMemberInfo, RaceWire, ServerPacket } from "@pd/shared";
import type { CtxItem } from "./context.js";
import { ChatPanel } from "./chat.js";
import { clearBubbles, drawChatBubbles, noteChat } from "./chatBubbles.js";
import type { ChatBubbleStore } from "./chatBubbles.js";
import { chatLineFor } from "./eventLines.js";
import { Hud, buildAbilities, buildEquipment, buildInventory } from "./hud.js";
import { raceHasClock, raceRowInfo } from "./raceRow.js";
import { buildBuffs } from "./buff-bar.js";
import type { InventoryEntry } from "./hud.js";
import { createGameModal } from "./modal.js";
import type { GameModal } from "./modal.js";
import { MapOverlay } from "./mapOverlay.js";
import type { Marker } from "./markers.js";
import { Viewport } from "./viewport.js";
import type { OverlayApi } from "./viewport.js";
import { loadHeroAssets, itemFrameUrl, playerTint } from "./hero.js";
import type { HeroAssets, PlayerAnim } from "./hero.js";
import { packHeroLut } from "./heroLut.js";
import type { PlayerLook } from "./render.js";
import { NetClient } from "./net.js";
import { traceItemObservation } from "./item-click-debug.js";
import type { ClientTick, GameplaySend, SessionInfo } from "./net.js";
import {
  attackLabel,
  corpseNear,
  enemyNear,
  enterTarget,
  interactApproach,
  isWalkable,
  nearestRevealedFloor,
  objectsAt,
  objectTitle,
  primaryAction,
  playerColor,
} from "./actions.js";
import type { PrimaryAction, Vec2, WorldObject } from "./actions.js";
import { examineObject, examinePlayer } from "./examine.js";
import { drawHpBar, enemyHeadRise } from "./combatChrome.js";
import { HALF_H, tileToScreen } from "./iso.js";
import { enemyKey, playerKey } from "./interpolate.js";
import { wizardOrbAtScreenPoint } from "./wizard-orbs.js";
import { doorTileAtScreenPoint, enemyCandidatesAtScreenPoint, enemyHitsAtScreenPoint, groundObjectTileAtScreenPoint } from "./hitbox.js";
import { clampHitDamage, hpBeforeSnapshot } from "./hitclamp.js";
import { formatRunRank, formatRunTime } from "./results.js";
import { floorPointAction, wizardOrbForPoint } from "./floor-point-action.js";

interface WireEvent {
  type?: unknown;
  tick?: unknown;
  playerId?: unknown;
  message?: unknown;
  reason?: unknown;
  x?: unknown;
  y?: unknown;
  itemId?: unknown;
  // item-dropped / sack-withdrawn / consumable-used (the (class, kind) pair)
  cls?: unknown;
  kind?: unknown;
  // floor end (run.md — the floor-ended event's payload)
  outcome?: unknown;
  mapPct?: unknown;
  players?: unknown;
  // combat (combat-hit / combat-miss / combat-death)
  attackerIsEnemy?: unknown;
  attackerId?: unknown;
  targetIsEnemy?: unknown;
  targetId?: unknown;
  damage?: unknown;
}

/** The floor-ended event's per-player row (run.md's results projection). */
interface EndedPlayer {
  id: number;
  kills: number;
  deaths: number;
  ended: "cleared" | "retreated" | "left";
}

/** Dev-only introspection for CDP probes (docs/browser-debugging.md pattern — the
 *  lab exposes __labState the same way). Read-only render/sim-view state;
 *  never touches shipping data paths (the seed is never in an observation).
 *  `positions`/`anims`/`markers` are written by the shared Viewport each
 *  frame; `obs`/`info`/`tick` by the game's onTick.
 *
 *  `ingestObs` + `hold`: the render-probe feed (docs/browser-debugging.md
 *  pattern) — a CDP probe can push a SYNTHETIC observation into the real
 *  viewport and hold it (server ticks skip the viewport ingest while held),
 *  so a specific render case (a boss block, a portal, a key on the base)
 *  can be screenshotted without walking the hero across the floor. The
 *  client still only renders: releasing the hold resumes the authoritative
 *  feed on the next tick, and nothing here touches a shipping path. */
const gameDebug: {
  obs: Observation | null;
  info: SessionInfo | null;
  tick: number;
  network?: NetClient["diagnostics"];
  positions: Map<string, Vec2>;
  anims: Map<string, PlayerAnim>;
  markers: Marker[];
  hover: Vec2 | null;
  ingestObs: ((o: Observation, tick: number) => void) | null;
  /** Live chat bubbles (speaker id -> bubble), for the probe: the same
   *  introspection the hub exposes. Read-only view. */
  bubbles: ChatBubbleStore;
  /** One immediate frame draw — see Viewport.redrawNow (headless rAF). */
  redrawNow: (() => void) | null;
  hold: boolean;
} = {
  obs: null,
  info: null,
  tick: 0,
  positions: new Map(),
  anims: new Map(),
  markers: [],
  hover: null,
  ingestObs: null,
  bubbles: new Map(),
  redrawNow: null,
  hold: false,
};
// @ts-expect-error — deliberate global for tooling
window.__game = gameDebug;

export class Game {
  private net: NetClient;
  private info: SessionInfo | null = null;
  /** The party panel's keyrace row on a floor (keyracing.md D2, 11-15): the same
   *  `RaceState` the town renders, and the same row — see `onRaceState`. */
  private races: RaceWire[] = [];
  /** Anchored on the transport, not here: walking through a door must not restart a
   *  clock that was already running down. */
  private get raceSeenAt(): number {
    return this.net.raceArrivedAt;
  }
  private raceClockTimer: number | null = null;
  private status: "waiting" | "running" | "complete" = "waiting";
  private party: { id: number; name: string; connected: boolean }[] = [];
  /**
   * Party looks: playerId → packed look, rebuilt from Welcome/RoomState
   * (the server sends each member's saved appearance there — NOT in the
   * observation; the sim never sees it). A member without an account is
   * simply absent from the map (the renderer keeps the identity tint).
   */
  private looks = new Map<number, PlayerLook>();

  private root: HTMLElement;
  private gameEl: HTMLDivElement;
  private canvas: HTMLCanvasElement;
  private viewport: Viewport;
  private hud: Hud;
  private chat: ChatPanel;
  /** Live in-world chat bubbles (speaker id -> bubble), shared with the town.
   *  Presentation only: the ChatPanel holds the transcript. */
  private readonly chatBubbles: ChatBubbleStore = new Map();
  private map: MapOverlay;
  /** Armed tile/item-targeted class ability. This is input state only; the
   * sim validates the eventual cast and remains authoritative. */
  private armedAbility: number | null = null;
  private pending: { obj: WorldObject; dest: Vec2; reachable: boolean } | null = null;
  private pendingEnter: { door: WorldObject & { kind: "door" }; dest: Vec2 } | null = null;
  /** "YOU DIED" banner shown once per death (the sim auto-respawns the hero
   *  at the base after the delay — the banner marks the death, not a wipe).
   *  Reset on a new RoomState. */
  private deadBannerShown = false;
  private eventAbort = new AbortController();
  private sackModal: { modal: GameModal; x: number; y: number; signature: string } | null = null;

  /** The exit-portal countdown bar (run.md): shown while a floor-end vote
   *  runs, with the remaining time + who has accepted. Hidden otherwise.
   *  A separate element from #banner — the countdown is persistent (it
   *  updates every tick) and must not fight the death/complete one-shots. */
  private exitEl: HTMLDivElement | null = null;

  /** The results screen (run.md): shown to everyone in the floor when it
   *  ends, from the floor-ended event (a projection — the event carries
   *  the outcome, map %, resolving tick, and the per-player snapshot). */
  private resultsEl: HTMLDivElement | null = null;
  /** The rank line (leaderboard.md): built hidden with the screen, filled
   *  by onRunRank (the server's rank arrives after the re-parent Welcome,
   *  while the screen is still up) — stays hidden on a retreat. */
  private rankEl: HTMLDivElement | null = null;
  /** How many of the listed players FINISHED (the bucket's party size,
   *  projected from the event — the same number the server used for the
   *  row). The floor name itself comes from the RunRank packet's floor id
   *  (resolved through FLOORS) — the client never re-derives it. */
  private resultsFinished = 0;
  private resultsTick = 0;
  /** True while the results screen is up: the hub's re-parent Welcome for
   *  this socket is QUEUED in index.ts and applied on dismiss, so the
   *  screen is seen before the town (FIFO on the wire: the floor-ended
   *  Tick precedes the hub Welcome). */
  private resultsPending = false;
  /** The host (index.ts) sets this when it queues the hub Welcome behind a
   *  pending results screen; dismiss calls it to land in the town. */
  onResultsDismissed: (() => void) | null = null;

  get resultsPendingFlag(): boolean {
    return this.resultsPending;
  }

  /** Sprite atlas for item-icon rendering (null until the async load resolves). */
  private heroAssets: HeroAssets | null = null;

  /** The latest authoritative observation lives in the Viewport (the shared
   *  renderer/interpolator); this getter keeps the `this.obs` call-sites intact. */
  private get obs(): Observation | null {
    return this.viewport.observation;
  }

  constructor(root: HTMLElement, net: NetClient, info: SessionInfo) {
    this.net = net;
    gameDebug.network = net.diagnostics;
    this.info = info;
    this.root = root;
    this.root.textContent = "";
    const game = document.createElement("div");
    game.id = "game";
    this.gameEl = game;
    this.canvas = document.createElement("canvas");
    game.appendChild(this.canvas);
    this.root.appendChild(game);

    // The shared viewport owns the renderer, the interpolation, the
    // hover/left-click/right-click input, and the frame loop. The game plugs
    // in what differs: primary action (walk/interact/enter), the context-menu
    // verbs, and lobby names.
    this.viewport = new Viewport(
      this.canvas,
      game,
      {
        meId: () => this.info?.playerId ?? null,
        primaryAction: (obs, me, x, y) => {
          if (this.armedAbility !== null) {
            const ability = CLASS_DEFS[me.classKind].abilities[this.armedAbility];
            if (!isWalkable(obs, x, y)) return { kind: "none" };
            if (ability?.kind === "telegrab") {
              const first = this.itemStackAt(obs, x, y)[0];
              return { kind: "walk", x, y, label: first ? `Telegrab the ${first.label}` : "Nothing to grab here" };
            }
            return { kind: "walk", x, y, label: `Cast ${ability?.name ?? "ability"} here` };
          }
          const a = primaryAction(obs, me, x, y);
          // run.md's exit verbs: the open portal's label depends on who is
          // clicking — the HOST's Interact ends the floor, anyone else's is
          // an acceptance (the countdown). The action is the same Interact;
          // only the label changes (the sim applies the host rule).
          if (a.kind === "interact" && a.obj.kind === "boss") {
            return { ...a, label: this.isHost() ? "End the floor" : "Vote to end" };
          }
          return a;
        },
        onPrimaryAction: (a) => this.executePrimary(a),
        pointTile: (obs, _me, point) => {
          if (objectsAt(obs, point.tile.x, point.tile.y).length > 0) return point.tile;
          if (this.armedAbility !== null) return null;
          return groundObjectTileAtScreenPoint(obs, point.sx, point.sy, point.isoUnit, point.camX, point.camY)
            ?? doorTileAtScreenPoint(this.heroAssets, obs, point.sx, point.sy, point.isoUnit, point.camX, point.camY);
        },
        pointAction: (obs, me, point, positions) => {
          const orb = wizardOrbForPoint(this.heroAssets, obs, point, positions);
          if (orb) return { kind: "interact", label: "Burst orb", obj: orb };
          if (this.armedAbility !== null) return null;
          return floorPointAction(this.heroAssets, obs, me, point, positions);
        },
        contextItemsAtPoint: (obs, me, point, positions) => {
          if (this.armedAbility !== null) return null;
          const orb = wizardOrbAtScreenPoint(obs, point.sx, point.sy, point.isoUnit, point.camX, point.camY);
          if (orb) return this.contextItemsForTile(obs, me, orb.x, orb.y);
          const hits = enemyHitsAtScreenPoint(
            this.heroAssets,
            obs,
            positions,
            point.sx,
            point.sy,
            point.isoUnit,
            point.camX,
            point.camY,
          );
          const candidates = enemyCandidatesAtScreenPoint(
            this.heroAssets,
            obs,
            positions,
            point.sx,
            point.sy,
            point.isoUnit,
            point.camX,
            point.camY,
          );
          const groundTile = objectsAt(obs, point.tile.x, point.tile.y).length > 0
            ? point.tile
            : groundObjectTileAtScreenPoint(obs, point.sx, point.sy, point.isoUnit, point.camX, point.camY);
          const doorTile = doorTileAtScreenPoint(this.heroAssets, obs, point.sx, point.sy, point.isoUnit, point.camX, point.camY);
          const objects = groundTile ? objectsAt(obs, groundTile.x, groundTile.y) : [];
          if (hits.length === 0) {
            if (candidates.length === 0 || objects.length > 0 || doorTile) return null;
            // The cursor is in a transparent frame margin. One candidate is
            // an unambiguous gap/ground click; two or more candidates are an
            // overlap, so the menu below enumerates them all.
            if (candidates.length === 1) {
              const candidate = candidates[0]!;
              if (objectsAt(obs, candidate.x, candidate.y).length > 0) {
                return this.contextItemsForTile(obs, me, candidate.x, candidate.y);
              }
              const dest = isWalkable(obs, point.tile.x, point.tile.y)
                ? point.tile
                : nearestRevealedFloor(obs, point.tile.x, point.tile.y);
              return {
                title: "Ground",
                items: [{
                  label: "Walk here",
                  kind: "walk",
                  disabled: !dest,
                  run: dest ? () => {
                    this.viewport.pushMenuMarker(null, dest);
                    this.sendMove(dest.x, dest.y);
                  } : undefined,
                }],
              };
            }
          }
          const targets = hits.length > 0 ? hits : candidates;
          const interactions: CtxItem[] = [];
          const examinations: CtxItem[] = [];
          // Match the sprite-first left click: Attack leads when the cursor
          // hits a live body. Keep covered loot available below it.
          for (const enemy of targets) {
            const name = enemyByKind(enemy.kind)?.name ?? "enemy";
            const al = attackLabel(name, enemy, obs.combat);
            interactions.push({
              label: al.label,
              tag: al.tag ?? undefined,
              kind: "interact",
              tone: "attack",
              run: () => {
                this.viewport.pushMenuMarker({ kind: "enemy", id: enemy.id, x: enemy.x, y: enemy.y });
                this.sendAttack(enemy.id);
              },
            });
            examinations.push({
              label: `Examine ${name}`,
              kind: "examine",
              run: () => this.chat.system(examineObject(obs, { kind: "enemy", id: enemy.id, x: enemy.x, y: enemy.y })),
            });
          }
          // A scene pick is a collection, not one winning tile. Include the
          // raw tile beneath the pointer and each hit NPC's tile, since an
          // item may sit beneath a body or on the ground its billboard covers.
          const objectTiles = [point.tile, ...(groundTile ? [groundTile] : []), ...(doorTile ? [doorTile] : []), ...targets.map((enemy) => ({ x: enemy.x, y: enemy.y }))];
          const seenTiles = new Set<string>();
          for (const tile of objectTiles) {
            const key = `${tile.x},${tile.y}`;
            if (seenTiles.has(key)) continue;
            seenTiles.add(key);
            if (objectsAt(obs, tile.x, tile.y).length === 0) continue;
            const menu = this.contextItemsForTile(obs, me, tile.x, tile.y);
            if (menu) {
              interactions.push(...menu.items.filter((item) => item.kind === "interact"));
              examinations.push(...menu.items.filter((item) => item.kind === "examine" && item.label !== "Examine self"));
            }
          }
          const items: CtxItem[] = [...interactions, ...examinations];
          const dest = isWalkable(obs, point.tile.x, point.tile.y)
            ? point.tile
            : nearestRevealedFloor(obs, point.tile.x, point.tile.y);
          if (dest) {
            items.push({
              label: "Walk here",
              kind: "walk",
              run: () => {
                this.viewport.pushMenuMarker(null, dest);
                this.sendMove(dest.x, dest.y);
              },
            });
          }
          return { title: "Choose option", items };
        },
        contextItems: (obs, me, x, y) => this.contextItemsForTile(obs, me, x, y),
        nameFor: (id) => this.playerName(id),
        lookFor: (id) => this.looks.get(id) ?? null,
        // Combat chrome: the hero's HP bar + a labelled bar over every live
        // enemy on the floor (shared drawHpBar — one implementation with the
        // arena's overlay pass), plus the party's chat bubbles.
        overlay: (o) => {
          this.drawCombatChrome(o);
          drawChatBubbles(
            { ctx: o.ctx, positions: o.positions, camX: o.camX, camY: o.camY, isoUnit: o.isoUnit, cssW: o.cssW, cssH: o.cssH, now: o.now },
            this.chatBubbles,
            this.heroAssets?.hero.anchor[1] ?? 1,
            true,
          );
        },
        onKey: (ev) => this.onKey(ev),
      },
      gameDebug,
    );

    this.hud = new Hud(game, true);
    this.chat = new ChatPanel(game);
    this.map = new MapOverlay(game, (id) => this.playerName(id));

    // Load the sprite atlas off the game path. Item cells are mounted without
    // text substitutes and repainted once the real atlas is available.
    void loadHeroAssets().then((a) => {
      if (!a) {
        this.hud.showBanner("Game art could not be loaded. Reload to retry.");
        return;
      }
      this.heroAssets = a;
      this.viewport.setHeroAssets(a);
      this.applyFloorLook();
      // M4: the inventory grid's icon slots draw the real item sprites
      // (PGS tinted by the stone's owner). Repaint existing cells because
      // their observation signature did not change while the atlas loaded.
      this.hud.onDrawIcon = (slot, iconId) => this.drawItemIcon(slot, iconId);
      this.hud.invalidateIcons();
    });

    // HUD events (the Viewport wires only the canvas input).
    this.chat.onSend = (text) => this.send({ op: ClientOp.Chat, message: text });
    this.hud.onItemMenu = (x, y, entry) => this.viewport.openMenu(x, y, entry.label, entry.menu);
    this.hud.onReorder = (keys, carried, items) => {
      this.send({ op: ClientOp.ReorderInventory, keys, carried, items });
    };

    // The exit countdown bar (run.md): a thin strip under the banner, above
    // the chat — persistent while a vote runs, updated every tick in onTick.
    this.exitEl = document.createElement("div");
    this.exitEl.id = "exit-countdown";
    this.exitEl.hidden = true;
    game.appendChild(this.exitEl);

    this.onRecovery(this.net.canPlay ? "live" : "syncing");
    this.chat.system("Find every room for a shot at the board. Slay the boss, then take the bright portal.");
    this.chat.system("Leaving early? Use Leave dungeon, or the dim portal near base. Your pocket stays here.");

    this.wireEvents();
    gameDebug.info = info;
    gameDebug.bubbles = this.chatBubbles;
    gameDebug.ingestObs = (o, t) => this.viewport.ingest(o, t);
    gameDebug.redrawNow = () => this.viewport.redrawNow();
    // @ts-expect-error — deliberate global for tooling
    window.__gameCam = () => this.viewport.camera;
  }

  // ---- public hooks (called by index.ts routing) -------------------------

  onWelcome(info: SessionInfo): void {
    this.info = info;
    gameDebug.info = info;
    this.updateLooks(info.party);
    this.onRecovery(this.net.canPlay ? "live" : "syncing");
    this.applyFloorLook();
    // The keyrace row survives the door (D2): the town's cached `RaceState` is
    // still the freshest race truth, and the server keeps broadcasting it to floor
    // sockets for exactly this reason — a party mid-run that cannot see its own
    // race reads as a race that silently died.
    this.races = [...this.net.raceState];
    this.updateRaceRow();
  }

  /**
   * The race state on a FLOOR (ServerOp.RaceState — keyracing.md D2). The row is
   * the whole of what a running race looks like: no window, no countdown to a
   * start that already happened, and nothing at all about the other side's dungeon
   * — two rooms, one seed, no shared space, so there IS nothing to show.
   *
   * Matching is by NAME, not by id: a side that opened its door has no town
   * presence, so every member of it arrives with `id: -1` (the wire's "nobody").
   * Names come from Hello and no two accounts share one; the roster the packet
   * carries is display-only, and no verb a client can send keys off it.
   */
  onRaceState(p: { races: RaceWire[] }): void {
    this.races = p.races;
    this.updateRaceRow();
  }

  /** One side forfeited (ServerOp.RaceForfeit, C3). A line in the floor's chat —
   *  the verdict screen (D1) is where the race is actually settled; this is only
   *  the fact that the race stopped waiting. The word is display: every forfeit
   *  reason scores the same, and a player told they forfeited with no reason reads
   *  it as a bug. */
  onRaceForfeit(p: { race: number; side: 0 | 1; reason: string }): void {
    const name = this.myRaceName();
    const race = this.races.find((r) => r.id === p.race);
    const mine = name !== null && race !== undefined
      && race.sides[p.side].members.some((m) => m.name === name);
    const why: Record<string, string> = {
      retreated: "the boss was still alive",
      incomplete: "the floor was not fully mapped",
      abandoned: "nobody was there when it ended",
      no_show: "the door was never opened",
    };
    this.chat.system(`Race over for ${mine ? "your side" : "the other side"} — ${why[p.reason] ?? p.reason}.`);
    this.updateRaceRow();
  }

  /** The name the race roster knows me by, or null. */
  private myRaceName(): string | null {
    const me = this.info?.playerId;
    if (me === undefined) return null;
    return this.party.find((m) => m.id === me)?.name
      ?? this.info?.party.find((m) => m.id === me)?.name
      ?? null;
  }

  private updateRaceRow(): void {
    const name = this.myRaceName();
    this.hud.renderRaceRow(name === null ? null : raceRowInfo({
      races: this.races,
      seenAt: this.raceSeenAt,
      isMe: (m) => m.name === name,
      // The packet's own roster is the only name source down here — the town's
      // roster is gone, and a side in its floor has no town presence to resolve.
      sideName: (race, side) => race.sides[side].members[0]?.name ?? "?",
      // The door is behind them: which side holds the host is not a fact a floor
      // row can act on, and the wire's `host` is -1 for anyone down here.
      hostIsMe: () => false,
    }));
    this.ensureRaceClock();
  }

  /** One second, text only: the row is signature-guarded, so a ticking clock must
   *  not rebuild it (traps/dom-rebuild.md). Reconciled with every row refresh, so
   *  a race that stops waiting on me (my side's outcome landed) stops the timer
   *  instead of ticking a clock nobody is reading. */
  private ensureRaceClock(): void {
    const name = this.myRaceName();
    const live = name !== null
      && this.races.some((r) => raceHasClock(r, (m) => m.name === name));
    if (!live) {
      if (this.raceClockTimer !== null) clearInterval(this.raceClockTimer);
      this.raceClockTimer = null;
      return;
    }
    if (this.raceClockTimer !== null) return;
    this.raceClockTimer = setInterval(() => {
      const name = this.myRaceName();
      if (name === null) return;
      const info = raceRowInfo({
        races: this.races,
        seenAt: this.raceSeenAt,
        isMe: (m) => m.name === name,
        sideName: (race, side) => race.sides[side].members[0]?.name ?? "?",
        hostIsMe: () => false,
      });
      if (info) this.hud.setRaceClock(info.clock.text, info.clock.tone);
    }, 1000) as unknown as number;
  }

  /** Rebuild the party looks from a server party list. Defensive re-parse
   *  (the server is trusted — this is the client-legibility gate, the same
   *  one the palette editor uses): malformed JSON never breaks the socket,
   *  it just renders the identity tint. */
  private updateLooks(members: PartyMemberInfo[]): void {
    const next = new Map<number, PlayerLook>();
    for (const m of members) {
      const app = m.appearance === undefined ? null : parseAppearance(m.appearance);
      if (!app) continue;
      // An all-canonical colour look packs to a null LUT — the member still
      // wears the CANON hero (their saved outfit included), not the
      // pre-account identity tint: having an account IS the switch.
      next.set(m.id, { lut: packHeroLut(app.colours), outfit: app.outfit });
    }
    this.looks = next;
  }

  /** The floor's tile shade: floorId -> roomId -> floorTile (the committed
   *  shared catalogs — no wire field; the plan's floor-shade resolution).
   *  Legacy no-floor rooms fall back to "stone" (the committed atlas
   *  default). */
  private resolveFloorPalette(): string {
    const floor = this.info?.floor;
    if (floor === undefined) return "stone";
    const roomId = floorById(floor)?.roomId;
    if (roomId === undefined || roomId === null) return "stone";
    return roomById(roomId)?.floorTile ?? "stone";
  }

  /** Presentation metadata is local catalog data: it never crosses the wire
   * and is deliberately absent from the simulation recipe. */
  private applyFloorLook(): void {
    const floor = this.info?.floor;
    const roomId = floor === undefined ? null : floorById(floor)?.roomId;
    const room = roomId === null || roomId === undefined ? null : roomById(roomId);
    this.viewport.setFloorLook(room?.floorTile ?? "stone", room?.architecture ?? "civic-stone-v1", room?.wear ?? 24);
  }

  onRoomState(p: Extract<ServerPacket, { op: ServerOp.RoomState }>): void {
    this.status = p.status;
    this.party = p.players;
    this.updateLooks(p.players);
    // The host is DERIVED server-side (floorHost — the lowest id still in
    // the floor and connected); a disconnect or a return-portal leave
    // transfers it and re-broadcasts this, so the exit verbs track the
    // transfer without a rejoin (run.md's host rule).
    if (this.info) this.info = { ...this.info, hostId: p.hostId };
    this.hud.renderParty(this.party, p.hostId, this.partyActions());
    this.deadBannerShown = false;
  }

  onRecoveredFloor(p: ClientTick): void {
    this.onTick(p);
    this.onResultsDisconnected();
  }

  onResultsDisconnected(): void {
    this.onRecovery("live"); // results remain readable while offline
    const button = this.resultsEl?.querySelector("button");
    if (button) button.textContent = "Sign in to return to town";
  }

  onTick(p: { tick: number; observation: Observation; events: unknown[]; resynced?: boolean }): void {
    // A dev render probe is holding a synthetic obs (gameDebug.hold) — keep
    // showing it instead of overwriting with the server feed.
    if (gameDebug.hold) return;
    if (p.resynced) {
      this.viewport.reset();
      this.pending = null;
      this.pendingEnter = null;
      this.armedAbility = null;
      this.viewport.closeMenu();
    }
    traceItemObservation(p.observation, this.info?.playerId, p.tick, p.events);
    // The overhit clamp (presentation only, hitclamp.ts): what each living
    // entity had BEFORE this obs lands — the hit tick's own observation
    // already shows post-hit HP. The clamped array feeds the effect layer
    // (the splat) and the chat line, so the displayed number matches the
    // bar: a 10 off a 1-HP mob reads "1", never "10".
    const prevObs = this.viewport.observation;
    const events = prevObs
      ? clampHitDamage(p.events, hpBeforeSnapshot(prevObs.enemies, prevObs.players))
      : p.events;
    this.viewport.ingest(p.observation, p.tick, events);
    this.hud.setTick(p.tick);
    this.hud.renderCombat(p.observation, this.info?.playerId ?? 0);
    // The hero's own row: the death banner's guard reads it (below). Active
    // buffs (`me.buffs`) and the class-defense windows render as the buff bar
    // (hud.renderBuffs over buff-bar.ts) — the surface the Status panel was
    // removed without.
    const me = p.observation.players.find((pl) => pl.id === this.info?.playerId);
    gameDebug.obs = p.observation;
    gameDebug.tick = p.tick;
    this.processEvents(events);
    // Recovery history may include an old death/respawn. The latest state
    // owns the banner, even when those transient events were missed.
    if (p.resynced && me) {
      this.deadBannerShown = !me.alive;
      if (me.alive) this.hud.hideBanner();
      else this.hud.showBanner("YOU DIED");
    }
    this.refreshHud();
    this.refreshSackModal();
    this.map.update(p.observation, this.info?.playerId ?? 0);
    this.updateExitCountdown(p.observation);
    // Arrived at the pending interact's approach tile → interact or error.
    if (!this.net.canPlay || this.myPlayer()?.iceSlide?.moving) { this.pending = null; this.pendingEnter = null; }
    if (this.pending) {
      const me = this.myPlayer();
      if (me && me.x === this.pending.dest.x && me.y === this.pending.dest.y) {
        const { obj, reachable } = this.pending;
        this.pending = null;
        if (reachable) {
          this.sendInteract(obj);
        } else {
          this.chat.invalid(REASON.cantReach);
        }
      }
    }
    // Arrived at the pending Enter's approach tile → walk through the door.
    if (this.pendingEnter) {
      const me = this.myPlayer();
      if (me && me.x === this.pendingEnter.dest.x && me.y === this.pendingEnter.dest.y) {
        const { door } = this.pendingEnter;
        this.pendingEnter = null;
        this.enterThrough(door);
      }
    }
  }

  onError(reason: string): void {
    this.chat.invalid(reason);
  }

  onClosed(): void {
    this.chat.system("Connection lost. Reconnecting…");
  }

  onRecovery(state: import("./net.js").RecoveryState): void {
    let notice = this.gameEl.querySelector<HTMLElement>(".connection-notice");
    if (!notice) {
      notice = document.createElement("div");
      notice.className = "connection-notice";
      notice.setAttribute("role", "status");
      this.gameEl.appendChild(notice);
    }
    notice.hidden = state === "live";
    notice.textContent = state === "syncing" ? "Syncing with the dungeon…" : state === "stalled" ? "Connection interrupted…" : "Reconnecting…";
    if (state !== "live") {
      this.pending = null;
      this.pendingEnter = null;
      this.armedAbility = null;
      this.viewport.closeMenu();
    }
  }

  destroy(): void {
    this.eventAbort.abort();
    if (this.raceClockTimer !== null) clearInterval(this.raceClockTimer);
    this.raceClockTimer = null;
    this.races = [];
    this.map.destroy();
    this.viewport.destroy();
    clearBubbles(this.chatBubbles);
    this.root.textContent = "";
  }

  // ---- internals ---------------------------------------------------------

  private myPlayer(): ObservedPlayer | null {
    if (!this.obs || !this.info) return null;
    return this.obs.players.find((p) => p.id === this.info!.playerId) ?? null;
  }

  /** Whether THIS player is the floor's host (run.md): the server derives
   *  it (floorHost — lowest id still in the floor and connected) and sends
   *  it in Welcome/RoomState; the client only reads it for the exit verbs'
   *  labels (the sim applies the rule — a non-host's "End" is a vote). */
  private isHost(): boolean {
    return this.info !== null && this.info.playerId === this.info.hostId;
  }

  /** The exit countdown bar (run.md): visible while a floor-end vote runs
   *  (obs.exit.remaining !== null), updated every tick — remaining time at
   *  20 ticks/s and who has accepted of the players still in the floor.
   *  Hidden once the vote resolves (the floor ends) or never started. */
  private updateExitCountdown(obs: Observation): void {
    const el = this.exitEl;
    if (!el) return;
    const remaining = obs.exit.remaining;
    if (remaining === null) {
      el.hidden = true;
      return;
    }
    const inFloor = new Set(obs.players.map((p) => p.id));
    const accepted = obs.exit.accepted.filter((id) => inFloor.has(id)).length;
    const total = obs.players.length;
    const secs = Math.ceil(remaining / 20); // 20 ticks = 1 s (the sim's clock)
    const mm = String(Math.floor(secs / 60));
    const ss = String(secs % 60).padStart(2, "0");
    el.hidden = false;
    el.textContent = `Floor closes in ${mm}:${ss}. Ready: ${accepted}/${total}`;
  }

  /** Display name for a player id: the lobby name (RoomState) takes priority
   *  over the sim's deterministic "Player N" default. */
  private playerName(id: number): string {
    const party = this.party.find((p) => p.id === id);
    if (party) return party.name;
    return this.obs?.players.find((p) => p.id === id)?.name ?? "?";
  }

  private refreshHud(): void {
    if (!this.obs || !this.info) return;
    // My own leave tick (the return portal / the floor ending on my leave):
    // the player-left has already removed me from the observation's players
    // (the sim's state is the source — I am gone), so there is no inventory
    // to render, and the scene is being torn down into the results screen /
    // hub anyway.
    if (!this.obs.players.some((p) => p.id === this.info!.playerId)) return;
    this.hud.renderInventory(buildInventory(this.obs, this.info.playerId, (id) => this.playerName(id)));
    this.hud.renderEquipment(buildEquipment(this.obs, this.info.playerId), this.obs.players.find((p) => p.id === this.info!.playerId)?.combatStats);
    this.hud.renderAbilities(buildAbilities(this.obs, this.info.playerId, gameDebug.tick, this.armedAbility));
    this.hud.renderBuffs(buildBuffs(this.obs, this.info.playerId, gameDebug.tick));
    // Party list keys (the RoomState roster has no keys — the observation
    // does; a member not in the obs (already re-parented out) holds none
    // visible to us). renderParty signature-guards the rebuild, so the
    // per-tick call is free while nothing changes.
    this.hud.renderParty(
      this.party.map((m) => {
        const op = this.obs!.players.find((p) => p.id === m.id);
        return { ...m, keys: op ? op.keys : [] };
      }),
      this.info.hostId,
      this.partyActions(),
    );
  }

  private partyActions() {
    return {
      meId: this.info!.playerId,
      leaveLabel: "Leave dungeon",
      onLeave: () => this.confirmLeave(() => this.send({ op: ClientOp.LeaveFloor })),
      openMenu: (x: number, y: number, title: string, items: { label: string; run: () => void }[]) => this.viewport.openMenu(x, y, title, items),
    };
  }

  /** Floor hotkeys. The hub mounts the same dock but sends no gameplay ops. */
  private onKey(ev: KeyboardEvent): void {
    if (ev.type !== "keydown" || ev.repeat) return;
    const target = ev.target;
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement ||
      target instanceof HTMLSelectElement ||
      (target instanceof HTMLElement && target.isContentEditable)
    ) return;
    if (ev.ctrlKey || ev.metaKey || ev.altKey) return;
    if (ev.key === "Enter") {
      if (target instanceof HTMLElement && target.closest('button, a, [role="button"], [role="dialog"]')) return;
      ev.preventDefault();
      this.chat.focusInput();
      return;
    }
    if (ev.key.toLowerCase() === "m") {
      ev.preventDefault();
      if (this.obs && this.info) this.map.toggle(this.obs, this.info.playerId);
      return;
    }
    if (ev.key === "Escape" && this.armedAbility !== null) {
      ev.preventDefault();
      this.armedAbility = null;
      this.refreshHud();
      return;
    }
    const slot = Number(ev.key) - 1;
    if (slot >= 0 && slot < 6) {
      ev.preventDefault();
      if (slot < 3) this.useAbility(slot);
      else {
        const me = this.myPlayer();
        if (!me) return;
        if (slot === 3 && me.pgs !== "inventory") window.dispatchEvent(new CustomEvent("pd-generate"));
        else if (slot === 4 && me.pgs === "deployed") window.dispatchEvent(new CustomEvent("pd-teleport", { detail: "personal" }));
        else if (slot === 5) window.dispatchEvent(new CustomEvent("pd-teleport", { detail: "group" }));
      }
    }
  }

  /** Click/key on a class slot: aimed kinds arm the world cursor; immediate
   * and next-hit kinds send at once. The sim re-checks class, level,
   * cooldown, equipment and targets. */
  private useAbility(abilityId: number): void {
    const me = this.myPlayer();
    if (!me) return;
    const ability = CLASS_DEFS[me.classKind].abilities[abilityId];
    if (!ability) return;
    if (TILE_AIMED_KINDS.includes(ability.kind)) {
      this.armedAbility = this.armedAbility === abilityId ? null : abilityId;
      this.refreshHud();
      return;
    }
    this.armedAbility = null;
    this.sendAbility(abilityId);
  }

  private sendAbility(
    abilityId: number,
    x?: number,
    y?: number,
    item?: { kind: "key" | "stone"; id: number },
  ): void {
    this.send({
      op: ClientOp.UseAbility,
      abilityId,
      targetId: item?.id ?? -1,
      ...(x !== undefined && y !== undefined ? { x, y } : {}),
      ...(item ? { targetKind: item.kind } : {}),
    });
  }

  private itemStackAt(
    obs: Observation,
    x: number,
    y: number,
  ): { kind: "key" | "stone"; id: number; label: string }[] {
    return objectsAt(obs, x, y)
      .filter(
        (o): o is Extract<WorldObject, { kind: "key" } | { kind: "stone" }> =>
          o.kind === "key" || o.kind === "stone",
      )
      .map((o) => ({ kind: o.kind, id: o.id, label: objectTitle(o, (id) => this.playerName(id)) }));
  }

  /** The game's combat chrome overlay (the Viewport's per-frame host pass):
   *  the hero's HP bar + a labelled bar over every live enemy on the floor.
   *  Dead combatants get no bar — the bones decal marks them. */
  private drawCombatChrome(o: OverlayApi): void {
    const hero = o.obs.players.find((p) => p.id === o.meId);
    if (!hero) return;
    if (this.armedAbility !== null && gameDebug.hover) {
      const tile = gameDebug.hover;
      const ability = CLASS_DEFS[hero.classKind].abilities[this.armedAbility];
      const inWorld = tile.x >= 0 && tile.y >= 0 && tile.x < o.obs.width && tile.y < o.obs.height;
      const floor = inWorld && o.obs.map[tile.y * o.obs.width + tile.x] === 1;
      const ok = ability?.kind === "telegrab" ? this.itemStackAt(o.obs, tile.x, tile.y).length > 0 : floor;
      const corner = (dx: number, dy: number) => tileToScreen(tile.x + dx, tile.y + dy, o.isoUnit, o.camX, o.camY);
      const top = corner(0, -0.5);
      const right = corner(0.5, 0);
      const bottom = corner(0, 0.5);
      const left = corner(-0.5, 0);
      o.ctx.save();
      o.ctx.strokeStyle = ok ? "#c9a6ff" : "rgba(224, 88, 74, 0.85)";
      o.ctx.lineWidth = 2;
      if (ok) {
        o.ctx.shadowColor = "#8b46ff";
        o.ctx.shadowBlur = 8;
      }
      o.ctx.beginPath();
      o.ctx.moveTo(top.x, top.y);
      o.ctx.lineTo(right.x, right.y);
      o.ctx.lineTo(bottom.x, bottom.y);
      o.ctx.lineTo(left.x, left.y);
      o.ctx.closePath();
      o.ctx.stroke();
      o.ctx.restore();
    }
    const heroPos = o.positions.get(playerKey(hero.id)) ?? { x: hero.x, y: hero.y };
    if (hero.alive) {
      drawHpBar(o.ctx, this.feetAt(heroPos.x, heroPos.y, o), "hero", hero.hp, hero.maxHp, 84);
    }
    for (const foe of o.obs.enemies) {
      if (!foe.alive) continue;
      const foePos = o.positions.get(enemyKey(foe.id)) ?? { x: foe.x, y: foe.y };
      drawHpBar(o.ctx, this.feetAt(foePos.x, foePos.y, o), enemyByKind(foe.kind)?.name ?? "enemy", foe.hp, foe.maxHp, enemyHeadRise(this.viewport.heroAssets, foe.kind, o.isoUnit));
    }
  }

  /** Tile coords → screen feet (the bar anchor), mirroring the arena's. */
  private feetAt(px: number, py: number, o: OverlayApi): { x: number; y: number } {
    const p = tileToScreen(px, py, o.isoUnit, o.camX, o.camY);
    return { x: p.x, y: p.y + HALF_H * o.isoUnit };
  }

  private wireEvents(): void {
    const options = { signal: this.eventAbort.signal };
    window.addEventListener("pd-ability", (e) => {
      this.useAbility((e as CustomEvent<number>).detail);
    }, options);
    window.addEventListener("pd-map-open", () => {
      if (this.obs && this.info) this.map.show(this.obs, this.info.playerId);
    }, options);
    window.addEventListener("pd-drop", (e) => {
      const id = (e as CustomEvent<number>).detail;
      this.send({ op: ClientOp.PlaceGatestone, stoneId: id });
    }, options);
    window.addEventListener("pd-drop-key", (e) => {
      const id = (e as CustomEvent<number>).detail;
      this.send({ op: ClientOp.DropKey, keyId: id });
    }, options);
    window.addEventListener("pd-equip", (e) => {
      const kind = (e as CustomEvent<number>).detail;
      this.send({ op: ClientOp.EquipItem, itemId: kind });
    }, options);
    window.addEventListener("pd-unequip", (e) => {
      const slot = (e as CustomEvent<EquipSlot>).detail;
      this.send({ op: ClientOp.UnequipItem, slot });
    }, options);
    window.addEventListener("pd-use-item", (e) => {
      // The pocket INDEX of the consumable (not the kind — the sim resolves
      // the (class, kind) entry at that slot; the same index the HUD's grid
      // position shows).
      const slot = (e as CustomEvent<number>).detail;
      this.send({ op: ClientOp.UseItem, slot });
    }, options);
    // Drop a POCKET item at the feet (DropItem): the HUD names the pocket
    // index; the sim resolves the (class, kind) entry there.
    window.addEventListener("pd-drop-item", (e) => {
      this.send({ op: ClientOp.DropItem, slot: (e as CustomEvent<number>).detail });
    }, options);
    // Drop a WORN piece: remove it (it lands at the END of the pocket),
    // then drop the entry that lands there. The two ops carry seq in send
    // order, so the sim applies them in order; if the pocket is full the
    // remove is refused and the drop names a dead slot — two chat errors,
    // no sim harm (the item stays worn).
    window.addEventListener("pd-drop-equipped", (e) => {
      const me = this.myPlayer();
      this.send({ op: ClientOp.UnequipItem, slot: (e as CustomEvent<EquipSlot>).detail });
      this.send({ op: ClientOp.DropItem, slot: me?.items.length ?? 0 });
    }, options);
    window.addEventListener("pd-generate", () => {
      this.send({ op: ClientOp.GenerateGatestone });
    }, options);
    window.addEventListener("pd-teleport", (e) => {
      const which = (e as CustomEvent<"personal" | "group">).detail;
      this.send({ op: ClientOp.TeleportGatestone, which });
    }, options);
  }

  private send(p: GameplaySend): void {
    this.net.sendGameplay(p);
  }

  /** M4: draw the real item sprite into an inventory cell's 48×48 icon
   *  slot. Stones tint by owner (PGS per-tint identity); everything else is
   *  canon. The <img> loads itself — a loaded-only gate would leave the
   *  first build of a cell empty (cells only rebuild on a signature
   *  change). Clears the slot first so a previous icon never leaks. */
  private drawItemIcon(slot: HTMLElement, entry: InventoryEntry): void {
    const assets = this.heroAssets;
    if (!assets || !entry.icon || !this.info) return;
    const tint =
      entry.icon === "gatestone-personal"
        ? playerTint(entry.ownerId ?? this.info.playerId)
        : "canon";
    const url = itemFrameUrl(assets, entry.icon, tint);
    if (!url) return;
    slot.replaceChildren();
    const img = document.createElement("img");
    img.src = url;
    // Size to the parent icon box (48px inventory cells, 32px equipment
    // cells) — pixelated scaling keeps the flat sprite crisp at both.
    img.style.width = "100%";
    img.style.height = "100%";
    img.style.objectFit = "contain";
    img.style.imageRendering = "pixelated";
    slot.append(img);
  }

  /** Open the base-room sack as a centered inventory-style modal. The modal
   * stays open while the authoritative observation removes withdrawn items. */
  private showSackModal(x: number, y: number): void {
    const sack = this.obs?.sack;
    if (!sack || sack.x !== x || sack.y !== y) return;
    if (this.sackModal?.modal.backdrop.isConnected) return;
    this.sackModal = null;
    this.viewport.closeMenu();
    const modal = createGameModal(this.gameEl, "Sack");
    this.sackModal = { modal, x, y, signature: JSON.stringify(sack.items) };
    this.renderSackContents();
  }

  /** Rebuild the sack grid only when its party-shared contents change. */
  private refreshSackModal(): void {
    const open = this.sackModal;
    if (!open) return;
    if (!open.modal.backdrop.isConnected) {
      this.sackModal = null;
      return;
    }
    const sack = this.obs?.sack;
    if (!sack || sack.x !== open.x || sack.y !== open.y) {
      open.modal.close();
      this.sackModal = null;
      return;
    }
    const signature = JSON.stringify(sack.items);
    if (signature === open.signature) return;
    open.signature = signature;
    this.renderSackContents();
  }

  private renderSackContents(): void {
    const open = this.sackModal;
    const sack = this.obs?.sack;
    if (!open || !sack || sack.x !== open.x || sack.y !== open.y) return;
    const grid = document.createElement("div");
    grid.className = "inventory-grid sack-grid";

    const slotCount = Math.max(4, Math.ceil(sack.items.length / 4) * 4);
    for (let i = 0; i < slotCount; i++) {
      const si = sack.items[i];
      if (!si) {
        const empty = document.createElement("div");
        empty.className = "cell empty";
        empty.setAttribute("aria-hidden", "true");
        grid.appendChild(empty);
        continue;
      }

      const def = si.cls === "equipment" ? itemByKind(si.kind) : consumableById(si.kind);
      const label = def?.name ?? "Unknown item";
      const cell = document.createElement("div");
      cell.className = "cell item";
      cell.setAttribute("role", "button");
      cell.tabIndex = 0;
      cell.title = `${label}. Take from sack.`;

      const icon = document.createElement("div");
      icon.className = "icon";
      if (def) {
        this.drawItemIcon(icon, {
          label,
          primary: "Take",
          icon: def.icon,
          itemKind: si.kind,
          itemCls: si.cls,
          menu: [],
        });
      }
      const cap = document.createElement("div");
      cap.className = "cap";
      cap.textContent = label;
      const primary = document.createElement("div");
      primary.className = "primary";
      primary.textContent = "Take";
      cell.append(icon, cap, primary);

      const take = (): void => {
        const target: WorldObject = { kind: "sack", x: open.x, y: open.y, itemId: si.id };
        this.viewport.pushMenuMarker(target);
        this.approachAndInteract(target);
      };
      cell.addEventListener("click", (ev) => {
        ev.stopPropagation();
        take();
      });
      cell.addEventListener("keydown", (ev) => {
        if (ev.key !== "Enter" && ev.key !== " ") return;
        ev.preventDefault();
        take();
      });
      grid.appendChild(cell);
    }

    open.modal.body.textContent = "";
    open.modal.body.appendChild(grid);
    if (sack.items.length === 0) {
      const empty = document.createElement("div");
      empty.className = "sack-empty";
      empty.textContent = "The sack is empty.";
      open.modal.body.appendChild(empty);
    }
  }

  /** What a left click at (x, y) does — the onPrimaryAction hook. Cancels any
   *  pending approach, then sends the intent (interact / enter / move). */
  private executePrimary(action: PrimaryAction): void {
    this.cancelPending();
    if (this.armedAbility !== null) {
      const abilityId = this.armedAbility;
      const me = this.myPlayer();
      const ability = me ? CLASS_DEFS[me.classKind].abilities[abilityId] : undefined;
      if ((action.kind === "walk" || action.kind === "attack") && ability) {
        if (ability.kind === "telegrab") {
          const first = this.obs ? this.itemStackAt(this.obs, action.x, action.y)[0] : undefined;
          if (!first) return; // keep the item picker armed
          this.sendAbility(abilityId, action.x, action.y, first);
        } else {
          this.sendAbility(abilityId, action.x, action.y);
        }
        this.armedAbility = null;
        this.refreshHud();
      }
      return;
    }
    switch (action.kind) {
      case "openSack":
        this.showSackModal(action.x, action.y);
        break;
      case "interact":
        // The return portal is the one verb with a CONSEQUENCE worth
        // confirming (run.md's leave confirmation): the pocket stays on the
        // floor. Everything else keeps the instant-intent model.
        if (action.obj.kind === "returnPortal") {
          this.confirmLeave(() => this.approachAndInteract(action.obj));
          return;
        }
        this.approachAndInteract(action.obj);
        break;
      case "enter":
        this.approachAndEnter(action.door);
        break;
      case "walk":
        this.sendMove(action.x, action.y);
        break;
      case "attack":
        this.sendAttack(action.target);
        break;
      case "self":
      case "none":
        break;
    }
  }

  /** The right-click context menu for a tile (the Viewport's contextItems
   *  hook) — the RuneScape interaction: every object on the tile gets its
   *  verb, plus Examine and (off your own tile) Walk here. Returns null when
   *  nothing should open (an unreachable, empty tile). */
  private contextItemsForTile(
    obs: Observation,
    me: ObservedPlayer,
    x: number,
    y: number,
  ): { title: string; items: CtxItem[] } | null {
    const objs = objectsAt(obs, x, y);
    const armed = this.armedAbility === null ? null : CLASS_DEFS[me.classKind].abilities[this.armedAbility];
    if (armed && armed.kind !== "telegrab") {
      this.armedAbility = null;
      this.refreshHud();
      return null;
    }
    if (objs.length > 0) {
      // Objects first — even when standing on the item's own tile (so a
      // stacked key/stone under your feet is still targetable).
      const items: CtxItem[] = [];
      const grab = CLASS_DEFS[me.classKind].abilities.find((ability) => ability.kind === "telegrab");
      const stack = this.itemStackAt(obs, x, y);
      if (grab && (this.armedAbility === null || armed?.kind === "telegrab")) {
        for (const item of stack) {
          items.push({
            label: `Telegrab the ${item.label}`,
            kind: "interact",
            tone: "pickup",
            run: () => {
              this.sendAbility(grab.id, x, y, item);
              this.armedAbility = null;
              this.refreshHud();
            },
          });
        }
      }
      for (const o of objs) {
        if (o.kind === "door") {
          items.push(
            o.open
              ? { label: "Enter", kind: "interact", run: () => { this.viewport.pushMenuMarker(o); this.approachAndEnter(o); } }
              : { label: "Open", kind: "interact", run: () => { this.viewport.pushMenuMarker(o); this.approachAndInteract(o); } },
          );
        } else if (o.kind === "returnPortal") {
          // run.md's leave confirmation — the pocket stays on the floor.
          items.push({
            label: "Leave the floor",
            kind: "interact",
            run: () => {
              this.viewport.pushMenuMarker(o);
              this.confirmLeave(() => this.approachAndInteract(o));
            },
          });
        } else if (o.kind === "wizardOrb") {
          items.unshift({ label: "Burst orb", kind: "interact", run: () => this.approachAndInteract(o) });
        } else if (o.kind === "chasmTile") {
          if (o.state === 0) items.push({ label: "Inspect tile", kind: "interact", run: () => { this.viewport.pushMenuMarker(o); this.approachAndInteract(o); } });
        } else if (o.kind === "iceTile") {
          // Movement, never an adjacency interaction; the sim commits the slide.
        } else if (o.kind === "puzzleTile") {
          if (!o.solved) {
            items.push({ label: "Imbue tile", kind: "interact", run: () => { this.viewport.pushMenuMarker(o); this.approachAndInteract(o); } });
            items.push({ label: "Force tile", kind: "interact", run: () => { this.viewport.pushMenuMarker(o); this.approachAndInteract({ ...o, force: true }); } });
          }
        } else if (o.kind === "sack") {
          // Primary click opens the centered modal; right-click keeps the
          // normal object menu with Open / Examine / Walk here.
          items.push({
            label: "Open",
            kind: "interact",
            run: () => this.showSackModal(x, y),
          });
        } else {
          // The open exit portal's verb tracks the host (run.md): the host's
          // Interact ends the floor, anyone else's is an acceptance. The
          // object itself only exists once the boss is dead — while it lives
          // the tile is plain floor and the boss NPC keeps its own Attack
          // menu (nothing in the room offers "Enter" until the kill).
          const label =
            o.kind === "boss"
              ? this.isHost()
                ? "End the floor"
                : "Vote to end"
              : `Take ${objectTitle(o, (id) => this.playerName(id))}`;
          items.push({
            label,
            kind: "interact",
            tone: o.kind === "boss" ? undefined : "pickup",
            run: () => { this.viewport.pushMenuMarker(o); this.approachAndInteract(o); },
          });
        }
      }
      const first = objs[0]!;
      for (const obj of objs) {
        items.push({
          label: `Examine ${objectTitle(obj, (id) => this.playerName(id))}`,
          kind: "examine",
          run: () => this.chat.system(examineObject(obs, obj)),
        });
      }
      if (me.x === x && me.y === y) {
        items.push({ label: "Examine self", kind: "examine", run: () => this.chat.system(examinePlayer(me)) });
      } else {
        const walk: CtxItem = {
          label: "Walk here",
          kind: "walk",
          run: () => {
            const dest = isWalkable(obs, x, y) ? { x, y } : nearestRevealedFloor(obs, x, y);
            if (dest) {
              this.viewport.pushMenuMarker(null, dest);
              this.sendMove(dest.x, dest.y);
            }
          },
        };
        // A bare crossing tile's primary click walks. Keep the menu in that order.
        if (first.kind === "chasmTile" && objs.length === 1) items.unshift(walk);
        else items.push(walk);
      }
      return { title: objectTitle(first, (id) => this.playerName(id)), items };
    }
    // A live enemy: Attack (the standing order — the sim chases and
    // auto-attacks; the click only declares intent) + Examine + Walk here.
    // Same body radius as the left click (enemyNear).
    const enemy = enemyNear(obs, x, y);
    if (enemy) {
      const name = enemyByKind(enemy.kind)?.name ?? "enemy";
      const al = attackLabel(name, enemy, obs.combat);
      const items: CtxItem[] = [
        {
          label: al.label,
          tag: al.tag ?? undefined,
          kind: "interact",
          tone: "attack",
          run: () => {
            // The menu is DOM, so its click never reaches the canvas — mirror
            // the left-click feedback (the enemy silhouette flash) manually.
            this.viewport.pushMenuMarker({ kind: "enemy", id: enemy.id, x: enemy.x, y: enemy.y });
            this.sendAttack(enemy.id);
          },
        },
        { label: "Examine", kind: "examine", run: () => this.chat.system(examineObject(obs, { kind: "enemy", id: enemy.id, x: enemy.x, y: enemy.y })) },
      ];
      if (isWalkable(obs, x, y)) {
        items.push({
          label: "Walk here",
          kind: "walk",
          run: () => { this.viewport.pushMenuMarker(null, { x, y }); this.sendMove(x, y); },
        });
      }
      return { title: name, items };
    }
    // A corpse: a floor object — Examine (client-side flavor; the future loot
    // verb will be a sim op) and Walk here (a corpse's tile is floor).
    const corpse = corpseNear(obs, x, y);
    if (corpse) {
      const obj: WorldObject = { kind: "corpse", id: corpse.id, x: corpse.x, y: corpse.y, npcKind: corpse.kind };
      const items: CtxItem[] = [
        { label: "Examine", kind: "examine", run: () => this.chat.system(examineObject(obs, obj)) },
      ];
      if (me.x === x && me.y === y) {
        items.push({ label: "Examine self", kind: "examine", run: () => this.chat.system(examinePlayer(me)) });
      } else {
        items.push({
          label: "Walk here",
          kind: "walk",
          run: () => { this.viewport.pushMenuMarker(null, { x, y }); this.sendMove(x, y); },
        });
      }
      return { title: objectTitle(obj, (id) => this.playerName(id)), items };
    }
    if (me.x === x && me.y === y) {
      return {
        title: this.playerName(me.id),
        items: [{ label: "Examine", kind: "examine", run: () => this.chat.system(examinePlayer(me)) }],
      };
    }
    const dest = isWalkable(obs, x, y) ? { x, y } : nearestRevealedFloor(obs, x, y);
    if (dest) {
      return {
        title: "Ground",
        items: [
          {
            label: "Walk here",
            kind: "walk",
            run: () => { this.viewport.pushMenuMarker(null, dest); this.sendMove(dest.x, dest.y); },
          },
        ],
      };
    }
    return null;
  }

  private approachAndInteract(obj: WorldObject): void {
    const me = this.myPlayer();
    if (!me || !this.obs) return;
    const approach = interactApproach(this.obs, me, obj);
    if (approach.tile.x === me.x && approach.tile.y === me.y) {
      if (approach.reachable) {
        this.sendInteract(obj);
      } else {
        this.chat.invalid(REASON.cantReach);
      }
      return;
    }
    this.pending = { obj, dest: approach.tile, reachable: approach.reachable };
    this.sendMove(approach.tile.x, approach.tile.y);
  }

  /** Click "Enter" on an open door: walk to the doorway's near side, then
   *  send the Enter move (the server walks through a revealed door or takes
   *  the open-door threshold jump into an unseen room). */
  private approachAndEnter(door: WorldObject & { kind: "door" }): void {
    const me = this.myPlayer();
    if (!me || !this.obs) return;
    const approach = interactApproach(this.obs, me, door);
    if (approach.tile.x === me.x && approach.tile.y === me.y) {
      if (approach.reachable) {
        this.enterThrough(door);
      } else {
        this.chat.invalid(REASON.cantReach);
      }
      return;
    }
    this.pendingEnter = { door, dest: approach.tile };
    this.sendMove(approach.tile.x, approach.tile.y);
  }

  private enterThrough(door: WorldObject & { kind: "door" }): void {
    const me = this.myPlayer();
    if (!me) return;
    const dest = enterTarget(me, door);
    this.sendMove(dest.x, dest.y);
  }

  private sendInteract(obj: WorldObject): void {
    if (obj.kind === "wizardOrb") { this.send({ op: ClientOp.Interact, kind: "wizard-orb", targetId: obj.id }); return; }
    if (obj.kind === "chasmTile") { this.send({ op: ClientOp.Interact, kind: "inspect-chasm", targetId: obj.id }); return; }
    if (obj.kind === "iceTile") return;
    if (obj.kind === "puzzleTile") {
      this.send({ op: ClientOp.Interact, kind: obj.force ? "puzzle-force" : "puzzle-imbue", targetId: obj.id });
      return;
    }
    // The return portal has no id of its own (one per floor) — the sim
    // resolves it from world.returnPortal; the fixed target is the shared
    // sentinel (RETURN_PORTAL_TARGET_ID) so the op stays a plain Interact.
    if (obj.kind === "returnPortal") {
      this.send({ op: ClientOp.Interact, kind: "portal", targetId: RETURN_PORTAL_TARGET_ID });
      return;
    }
    if (obj.kind === "item") {
      // A ground drop: the drop's entity id names the SPECIFIC item of the
      // pile (design/loot.md — the server takes that id, not "the top").
      this.send({ op: ClientOp.Interact, kind: "item", targetId: obj.id });
      return;
    }
    if (obj.kind === "sack") {
      // A sack withdrawal: the SACK ITEM id (the menu's choice — the tile
      // object's -1 is a menu opener, never routed here; only the menu's
      // per-item targets reach sendInteract).
      this.send({ op: ClientOp.Interact, kind: "sack", targetId: obj.itemId });
      return;
    }
    const kind: InteractKind = obj.kind === "boss" ? "boss" : obj.kind === "door" ? "door" : obj.kind === "key" ? "key" : "stone";
    this.send({ op: ClientOp.Interact, kind, targetId: obj.id });
  }

  /** The standing attack order: the sim chases the target and auto-attacks
   *  on the weapon's cadence until it dies (or the order is replaced). */
  private sendAttack(targetId: number): void {
    this.send({ op: ClientOp.Attack, targetId, targetIsEnemy: true });
  }

  /** Send a Move. Markers are spawned once at pointer-down by the Viewport —
   *  later legs (interact approach, doorway Enter) send plain moves with no
   *  marker side effects. */
  private sendMove(x: number, y: number): void {
    this.send({ op: ClientOp.Move, x, y });
  }

  private cancelPending(): void {
    this.pending = null;
    this.pendingEnter = null;
  }

  /** Apply one tick's events: the chat line (eventLines.ts decides which
   *  events speak at all), the banner, the results screen, and the routing
   *  cancel. The line text never lives here — it is the tested rule. */
  private processEvents(events: unknown[]): void {
    if (!this.obs) return;
    const me = this.info?.playerId ?? -1;
    for (const raw of events) {
      const e = raw as WireEvent;
      if (typeof e !== "object" || e === null) continue;
      const type = e.type;
      if (typeof type !== "string") continue;
      const pid = typeof e.playerId === "number" ? e.playerId : -1;

      const line = chatLineFor(e, {
        player: (id) => this.playerName(id),
        isMe: (id) => id === me,
        item: (cls, kind) => (cls === "consumable" ? consumableById(kind) : itemByKind(kind))?.name ?? null,
      });
      if (line) {
        if (line.kind === "party") {
          this.chat.party(line.name ?? "?", line.text, playerColor(pid));
          // The bubble is the same line, in the world, over the speaker. It
          // draws only for a hero on screen; a teammate in another room still
          // gets their line in the log.
          noteChat(this.chatBubbles, pid, line.text);
        } else if (line.kind === "feedback") {
          if (pid === me) this.chat.invalid(line.text);
        } else {
          this.chat.system(line.text);
        }
      }

      switch (type) {
        case "floor-ended":
          // run.md: the floor is over for everyone in it — the results
          // screen projects this event (outcome, map %, the resolving tick,
          // the per-player snapshot). The room closes on the server right
          // after this tick; the town's Welcome lands behind the screen.
          this.showResults(e);
          break;
        case "combat-death": {
          // The banner belongs to the death itself, not to the line: an enemy
          // death says nothing here, a hero death raises the flag.
          const tgt = typeof e.targetId === "number" ? e.targetId : -1;
          if (e.targetIsEnemy !== true && tgt === me && !this.deadBannerShown) {
            this.deadBannerShown = true;
            this.hud.showBanner("YOU DIED");
          }
          break;
        }
        case "player-respawned":
          // The death banner marks the death window, not a wipe: the hero is
          // back, so clear it (and re-arm it — a later death must re-show).
          if (pid === me) {
            this.hud.hideBanner();
            this.deadBannerShown = false;
          }
          break;
        case "invalid":
          // A rejected walk never resolves the pending interact/enter
          // approach, so cancel it (markers themselves are input acks —
          // R1: never retroactively removed). Compared by identity against
          // the shared reason table, never by substring.
          if (pid === me && e.reason === REASON.walkBlocked) {
            this.pending = null;
            this.pendingEnter = null;
          }
          break;
      }
    }
  }

  /** The results screen (run.md): shown to everyone in the floor when it
   *  ends. A pure projection of the floor-ended event — outcome word, map
   *  % (room-level fog makes it exact), the resolving tick as mm:ss at
   *  20 tps, and the per-player snapshot (kills, deaths, how they ended).
   *  The worn loadout is banked but isn't relevant to this screen. Rank (CLEARED
   *  and 100%-mapped only) arrives with the leaderboard slice — hidden until then.
   *  Dismiss lands the party back in the hub (index.ts queued the Welcome). */
  private showResults(e: WireEvent): void {
    if (this.resultsEl) return; // one screen per floor end
    this.resultsPending = true;

    const outcome = e.outcome === "cleared" ? "cleared" : "retreated";
    const mapPct = typeof e.mapPct === "number" ? e.mapPct : 0;
    const tick = typeof e.tick === "number" ? e.tick : 0;
    this.resultsTick = tick;

    const rows = Array.isArray(e.players) ? (e.players as unknown[]) : [];
    const me = this.info?.playerId ?? -1;

    const el = document.createElement("div");
    el.id = "results";

    const h1 = document.createElement("h1");
    h1.textContent = outcome === "cleared" ? "FLOOR CONQUERED" : "THE PARTY RETREATED";
    h1.className = outcome === "cleared" ? "cleared" : "retreated";
    el.appendChild(h1);

    const sub = document.createElement("div");
    sub.className = "results-sub";
    sub.textContent = `${mapPct}% explored  ·  ${formatRunTime(tick)}`;
    el.appendChild(sub);

    // The rank line (leaderboard.md): right under the time it belongs
    // next to. Hidden until the server's RunRank lands (qualified clears
    // only); a lower-coverage clear shows its DQ immediately instead.
    this.rankEl = document.createElement("div");
    this.rankEl.id = "results-rank";
    this.rankEl.hidden = !(outcome === "cleared" && mapPct < 100);
    if (!this.rankEl.hidden) {
      this.rankEl.className = "dq";
      this.rankEl.textContent = "Board DQ: explore the whole map to qualify.";
    }
    el.appendChild(this.rankEl);
    this.resultsFinished = 0;

    const table = document.createElement("table");
    table.className = "results-table";
    const thead = document.createElement("thead");
    const headRow = document.createElement("tr");
    for (const h of ["Player", "Kills", "Deaths", "Fate"]) {
      const th = document.createElement("th");
      th.textContent = h;
      headRow.appendChild(th);
    }
    thead.appendChild(headRow);
    table.appendChild(thead);
    const tbody = document.createElement("tbody");
    for (const raw of rows) {
      if (typeof raw !== "object" || raw === null) continue;
      const p = raw as Partial<EndedPlayer>;
      if (typeof p.id !== "number") continue;
      const tr = document.createElement("tr");
      if (p.id === me) tr.className = "me";
      const nm = document.createElement("td");
      nm.textContent = this.playerName(p.id);
      tr.appendChild(nm);
      const kills = document.createElement("td");
      kills.textContent = String(typeof p.kills === "number" ? p.kills : 0);
      tr.appendChild(kills);
      const deaths = document.createElement("td");
      deaths.textContent = String(typeof p.deaths === "number" ? p.deaths : 0);
      tr.appendChild(deaths);
      const ended = document.createElement("td");
      ended.textContent = p.ended === "cleared" ? "Conquered"
        : p.ended === "retreated" ? "Turned back"
        : p.ended === "left" ? "Left early"
        : "";
      ended.className = `ended-${p.ended ?? ""}`;
      tr.appendChild(ended);
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    el.appendChild(table);

    // The bucket's party size: who FINISHED, projected from the event's
    // per-player `ended` (the same count the server used for its row).
    for (const raw of rows) {
      if (typeof raw === "object" && raw !== null && (raw as Partial<EndedPlayer>).ended === "cleared") this.resultsFinished++;
    }

    const btn = document.createElement("button");
    btn.textContent = "Head to the hub";
    btn.addEventListener("click", () => this.dismissResults());
    el.appendChild(btn);

    this.root.appendChild(el);
    this.resultsEl = el;
  }

  /**
   * The run's rank (leaderboard.md): the server's post for the floor that
   *  just ended — it lands right after the re-parent Welcome, while the
   *  results screen is still up (index.ts holds the Welcome behind it). A
   *  null pair = a RETREATED or DQ floor (no time to rank): the line stays
   *  hidden or keeps its DQ note. One row per qualified clear, so the rank is party-uniform — every
   *  player in the floor sees the same placement and nearby times.
   */
  onRunRank(p: Extract<ServerPacket, { op: ServerOp.RunRank }>): void {
    if (!this.rankEl) return;
    if (p.rank === null || p.of === null) {
      if (p.runId !== undefined) {
        this.rankEl.querySelector("a")?.remove();
        this.rankEl.appendChild(this.runDetailLink(p.runId));
        this.rankEl.hidden = false;
      }
      return;
    }
    const floorName = floorById(p.floor)?.name ?? "";
    this.rankEl.textContent = "";
    const title = document.createElement("div");
    title.className = "results-rank-title";
    title.textContent = formatRunRank(p.rank, p.of, floorName, this.resultsFinished, this.info?.size);
    const nearby = document.createElement("div");
    nearby.className = "results-nearby";
    const timeRow = (label: string, rank: number | null, ticks: number | null | undefined, own = false): void => {
      const row = document.createElement("div");
      row.className = "results-nearby-row" + (own ? " own" : "");
      const place = document.createElement("span");
      place.textContent = rank === null ? "" : `${own ? "> " : ""}#${rank}`;
      const name = document.createElement("span");
      name.textContent = label;
      const time = document.createElement("span");
      time.textContent = ticks == null ? "" : formatRunTime(ticks);
      row.append(place, name, time);
      nearby.appendChild(row);
    };
    if (p.fasterTicks != null) timeRow("Just ahead", p.fasterRank, p.fasterTicks);
    timeRow("Your run", p.rank, this.resultsTick, true);
    if (p.slowerTicks != null) timeRow("Just behind", p.slowerRank, p.slowerTicks);
    this.rankEl.append(title, nearby);
    if (p.runId !== undefined) this.rankEl.appendChild(this.runDetailLink(p.runId));
    this.rankEl.hidden = false;
  }

  private runDetailLink(id: number): HTMLAnchorElement {
    const link = document.createElement("a");
    link.className = "results-detail-link";
    link.href = `/run?id=${id}`;
    link.dataset.records = "";
    link.textContent = "View and share floor details →";
    return link;
  }

  /** Dismiss the results screen: the floor's over for this socket — apply
   *  the queued hub re-parent (index.ts) and land the party in the town. */
  private dismissResults(): void {
    if (!this.resultsPending) return;
    this.resultsPending = false;
    this.resultsEl?.remove();
    this.resultsEl = null;
    const exit = this.exitEl;
    if (exit) exit.hidden = true;
    const cb = this.onResultsDismissed;
    this.onResultsDismissed = null;
    cb?.();
  }

  /** The leave confirmation (run.md): the return portal ends YOUR floor —
   *  the pocket (keys, carried items) is left on the floor, the worn gear
   *  comes with you, and the party carries on without you. One modal, no
   *  wall clock; the sim is the authority (the server rejects the Interact
   *  with an Error line if anything is off, e.g. still holding the GGS). */
  private confirmLeave(onConfirm: () => void): void {
    const modal = createGameModal(this.gameEl, "Leave the floor?", "leave-confirm");

    const body = document.createElement("div");
    body.className = "leave-body";
    body.textContent =
      "Head back alone? Your pocket stays on this floor and your worn gear comes with you. " +
      "The party can keep going.";
    modal.body.appendChild(body);

    const row = document.createElement("div");
    row.className = "leave-row";
    const stay = document.createElement("button");
    stay.textContent = "Stay";
    stay.className = "leave-stay";
    stay.addEventListener("click", modal.close);
    const leave = document.createElement("button");
    leave.textContent = "Leave";
    leave.className = "leave-go";
    leave.addEventListener("click", () => {
      modal.close();
      onConfirm();
    });
    row.appendChild(stay);
    row.appendChild(leave);
    modal.body.appendChild(row);
  }
}
